import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  FeeFineDetailsData,
  StudentPaymentSchemeWithMetrics,
  PaymentSchemeDiscounts,
  PaymentAddonsStatus
} from '../../../types/paymentScheme';
import { PaymentUser } from '../../../types/userType';
import { StyledButton } from '../styled';
import { usersWithConcessionApprovalAccess } from '../../../constants/superAdminUsers';

export const getFeeFineDetailsColumns = (
  feeFineDetails: FeeFineDetailsData[],
  onApproveRejectClick: (scheme: StudentPaymentSchemeWithMetrics) => void,
  paymentUser: PaymentUser
): GridColDef<FeeFineDetailsData>[] => {
  const firstFeeFineDetail = feeFineDetails[0];
  const totalLateFee = firstFeeFineDetail?.totalLateFee || 0;
  const totalChequeBounceFee = firstFeeFineDetail?.totalChequeBounceFee || 0;
  const totalPaidLateFee = firstFeeFineDetail?.totalLateFeePaid || 0;
  const totalPaidChequeBounceFee =
    firstFeeFineDetail?.totalChequeBounceFeePaid || 0;
  const totalFinePayable = firstFeeFineDetail?.totalFinePayable || 0;

  const baseColumns: GridColDef<FeeFineDetailsData>[] = [
    { field: 'particulars', headerName: 'Particulars', width: 180 },
    { field: 'feePeriod', headerName: 'Fee Period', width: 120 },
    { field: 'academicYear', headerName: 'Academic Year', width: 120 },
    {
      field: 'lateFee',
      headerName: `Late Fee (${totalLateFee})`,
      type: 'number',
      width: 200
    },
    {
      field: 'paidLateFee',
      headerName: `Paid Late Fee (${totalPaidLateFee})`,
      type: 'number',
      width: 200
    },
    {
      field: 'chequeBounceFee',
      headerName: `Cheque Bounce Fee (${totalChequeBounceFee})`,
      type: 'number',
      width: 200
    },
    {
      field: 'paidChequeBounceFee',
      headerName: `Paid Cheque Bounce Fee (${totalPaidChequeBounceFee})`,
      type: 'number',
      width: 200
    },
    {
      field: 'pendingFinePayable',
      headerName: `Total Fine Payable (${totalFinePayable})`,
      type: 'number',
      width: 200
    },
    { field: 'dueDays', headerName: 'Due Days', type: 'number', width: 100 },
    {
      field: 'dueMonths',
      headerName: 'Due Months',
      type: 'number',
      width: 120
    },
    {
      field: 'waiversString',
      headerName: 'Waivers',
      width: 280,
      minWidth: 240,
      flex: 1
    },
    {
      field: 'concessionsString',
      headerName: 'Concessions',
      width: 360,
      minWidth: 280,
      flex: 1
    }
  ];

  const customColumns: GridColDef<FeeFineDetailsData>[] = [];

  if (usersWithConcessionApprovalAccess.includes(paymentUser?.email)) {
    customColumns.push({
      field: 'concessionsAndWaiversAction',
      headerName: 'Concession/Waiver',
      width: 180,
      renderCell: (params: GridRenderCellParams<FeeFineDetailsData>) => {
        const hasAdditionalConcessionsOrWaiversInApproval =
          params.row.additionalConcessionsAndWaivers?.some(
            (item) => item.status === PaymentAddonsStatus.IN_APPROVAL
          );

        return (
          <StyledButton
            variant='contained'
            size='small'
            onClick={() =>
              onApproveRejectClick(
                params.row as unknown as StudentPaymentSchemeWithMetrics
              )
            }
            disabled={!hasAdditionalConcessionsOrWaiversInApproval}
          >
            Approve/Reject
          </StyledButton>
        );
      }
    });
  }

  return [...baseColumns, ...customColumns];
};

export const generateFeeFineDetails = (
  studentPaymentSchemes: StudentPaymentSchemeWithMetrics[]
): FeeFineDetailsData[] => {
  const lateFeeDetails: FeeFineDetailsData[] = [];
  studentPaymentSchemes.forEach((paymentScheme) => {
    if (paymentScheme.lateFee > 0 || paymentScheme.chequeBounceFee > 0) {
      lateFeeDetails.push({
        _id: paymentScheme._id,
        particulars: paymentScheme.particulars,
        feePeriod: paymentScheme.feePeriod,
        academicYear: paymentScheme.academicYear,
        chequeBounceFee: paymentScheme.chequeBounceFee,
        lateFee: paymentScheme.lateFee,
        paidLateFee: paymentScheme.paidLateFee,
        pendingLateFee: paymentScheme.pendingLateFee,
        paidChequeBounceFee: paymentScheme.paidChequeBounceFee,
        pendingChequeBounceFee: paymentScheme.pendingChequeBounceFee,
        pendingFinePayable: paymentScheme.pendingFinePayable,
        dueDays: paymentScheme.dueDays,
        dueMonths: paymentScheme.dueMonths,
        dueDate: paymentScheme.paymentDueDate,
        additionalConcessionsAndWaivers:
          paymentScheme.additionalConcessionsAndWaivers,
        totalLateFee: paymentScheme.totalLateFee,
        totalLateFeePaid: paymentScheme.totalLateFeePaid,
        totalChequeBounceFee: paymentScheme.totalChequeBounceFee,
        totalChequeBounceFeePaid: paymentScheme.totalChequeBounceFeePaid,
        totalFinePayable: paymentScheme.totalFinePayable,
        waiversString:
          paymentScheme.additionalConcessionsAndWaivers
            ?.filter(
              (discount) =>
                discount.type === PaymentSchemeDiscounts.WAIVERS &&
                ![PaymentAddonsStatus.REJECTED].includes(discount.status || '')
            )
            .map((waiver) => `${waiver.description} - ${waiver.amount}`)
            .join(', ') || '',
        concessionsString:
          paymentScheme.additionalConcessionsAndWaivers
            ?.filter(
              (discount) =>
                discount.type === PaymentSchemeDiscounts.CONCESSIONS &&
                ![PaymentAddonsStatus.REJECTED].includes(discount.status || '')
            )
            .map(
              (concession) => `${concession.description} - ${concession.amount}`
            )
            .join(', ') || ''
      });
    }
  });
  return lateFeeDetails;
};
